import { Component } from '@theme/component';

/**
 * Slider shown inside the technical details modal.
 *
 * The component only owns the modal. Thumbnails stay in the grid/accordion and
 * point to it with `on:click="#<component-id>/open/<slide-index>"`, following the
 * same pattern as `zoom-dialog`.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLElement} swiper - The Swiper container inside the dialog.
 *
 * @extends {Component<Refs>}
 */
export class TechnicalDetails extends Component {
  requiredRefs = ['dialog', 'swiper'];

  /**
   * Swiper is created on the first open: while the dialog is hidden it has no
   * measurable layout, so creating it earlier would leave the slider stuck.
   *
   * @type {any}
   */
  #swiper = null;

  updatedCallback() {
    super.updatedCallback();

    if (!this.#swiper) return;

    // A morph (Section Rendering API) can replace the slider element, leaving
    // the instance bound to a detached node.
    if (this.#swiper.el !== this.refs.swiper) this.#destroySwiper();
    else this.#swiper.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwiper();
  }

  /**
   * Opens the modal on the requested slide.
   *
   * @param {number} index - Zero-based slide index.
   * @param {Event} [event] - Click event from the `on:click` handler.
   */
  open(index, event) {
    const slideIndex = Number(index);
    if (!Number.isInteger(slideIndex) || slideIndex < 0) return;

    // The thumbnails keep `commandfor` as a no-JS fallback; when this handler
    // runs, the component owns the opening sequence instead.
    event?.preventDefault();

    const { dialog } = this.refs;
    if (!dialog) return;

    if (!dialog.open) dialog.showModal();

    const swiper = this.#ensureSwiper();
    if (!swiper) return;

    // Re-measure now that the dialog is visible, then move to the clicked slide.
    swiper.update();
    swiper.slideToLoop(slideIndex, 0);
  }

  /**
   * @returns {any} The Swiper instance, or null when Swiper is unavailable.
   */
  #ensureSwiper() {
    if (this.#swiper) return this.#swiper;

    const SwiperClass = window.Swiper;
    const { swiper } = this.refs;
    if (!SwiperClass || !(swiper instanceof HTMLElement)) return null;

    this.#swiper = new SwiperClass(swiper, {
      loop: true,
      speed: 400,
      grabCursor: true,
      keyboard: { enabled: true },
      navigation: {
        nextEl: swiper.querySelector('.swiper-button-next'),
        prevEl: swiper.querySelector('.swiper-button-prev'),
      },
    });

    return this.#swiper;
  }

  #destroySwiper() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('technical-details-component')) {
  customElements.define('technical-details-component', TechnicalDetails);
}
